// @givmo/board — written-consent decode + display helpers.
//
// ══ THE STANDING LINE IS DISPLAYED, NEVER COMPOSED ══════════════════════════════════════════════
//
// `standing_statement` is a sentence the BACKEND renders, from one place, precisely so a consent one
// signature short always reads the same way — *pending*, with its count (plan §4, restated by
// CBG-FIX27). Every surface here
// puts it on screen verbatim. There is deliberately no helper that turns `signed_count`/`required_count`
// into a sentence of this app's own: a second phrasing of the one fact §4 governs is exactly the drift
// the single renderer exists to prevent, and "3 of 4 — nearly there" is a conclusion the product may
// not draw about an instrument that is not yet an act of the board.
//
// `is_an_act_of_the_board` is the same fact as a boolean, for branching. It is false for every status
// but `executed`.
//
// ══ WHOSE SIGNATURE COUNTS IS THE BACKEND'S REDUCTION, NOT OURS ═════════════════════════════════
//
// A revocation is an APPENDED act, never an edit — so a seat's standing is its MOST RECENT act, a
// reduction the backend does in exactly one place (and its execution trigger repeats in SQL, tiebreaker
// included). `BoardConsentRosterMemberOut.has_signed` is that reduction's answer. This module reads it
// and never re-derives one: a second implementation of "who has signed" that disagreed by a tiebreaker
// would put a different unanimity on screen from the one the database will enforce.
//
// The signature LOG is still rendered in full, revocations included, because that is the §1.2
// provability set the officer is entitled to see. Rendering the log is not the same as recomputing
// standing from it.

import { extractList } from '@givmo/api-client/list';
import { displayClockTime, humanize } from '../../lib/display';
import { factorLabel } from '../../lib/factor-words';
import { seatConnection } from '../roster/roster-decode';
// Re-exported so every consent surface keeps importing its formatting from its own decode module,
// while there is exactly one definition of each (`lib/display.ts`).
export { displayDate, displayInstant, todayAsDateInput } from '../../lib/display';
import type {
  BoardConsentRosterMemberOut,
  BoardConsentSignatureOut,
  BoardConsentTemplateOut,
  OrgBoardDirectorConsentOut,
  OrgBoardMemberOut,
  OrgBoardWrittenConsentOut,
} from '@givmo/api-client';
import type { BadgeProps } from '@givmo/design-system';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';

type BadgeTone = NonNullable<BadgeProps['tone']>;

/**
 * Consents from either principal's list route. Both handlers are annotated `-> dict`, so the wire is
 * the paginated envelope, which the api-client middleware unwraps to a bare array at runtime —
 * `extractList` reads either shape, or `[]`, and never throws.
 */
export function extractConsents(body: unknown): OrgBoardWrittenConsentOut[] {
  return extractList<OrgBoardWrittenConsentOut>(body, 'data');
}

/** The narrower shape a director sees — a separate model on the wire, so a separate reader here. */
export function extractDirectorConsents(body: unknown): OrgBoardDirectorConsentOut[] {
  return extractList<OrgBoardDirectorConsentOut>(body, 'data');
}

/** Forms from the consent/resolution library. */
export function extractTemplates(body: unknown): BoardConsentTemplateOut[] {
  return extractList<BoardConsentTemplateOut>(body, 'data');
}

/** One consent waiting on this director, read off the attention list. */
export interface ConsentAwaitingDirector {
  consentId: string;
  title: string;
  consentDate: string | null;
  /** CBG-YR6-CONSENT: the committee acting, or null for the board's (and an answer without it). */
  committeeName: string | null;
}

/**
 * The consents in a director's attention list.
 *
 * ⚠ `items` is a list of PLAIN DICTS on the wire, not one model, and deliberately so: entries of
 * different kinds carry different fields, and the shape was shipped structurally complete so later
 * items could fill it with no wire change — which is exactly what happened three times. So this reader
 * is written the way a tolerant reader of an open list has to be: it selects on the `kind`
 * discriminator, reads only the fields its own kind promises, and DROPS anything malformed rather than
 * rendering a half-row. An unknown kind is not an error here; it is a newer backend, and this surface's
 * job is to ignore it rather than to break on it.
 */
export function consentsAwaitingDirector(items: unknown): ConsentAwaitingDirector[] {
  if (!Array.isArray(items)) return [];
  const awaiting: ConsentAwaitingDirector[] = [];
  for (const raw of items) {
    if (typeof raw !== 'object' || raw === null) continue;
    const item = raw as Record<string, unknown>;
    if (item.kind !== 'written_consent') continue;
    const consentId = typeof item.reference_id === 'string' ? item.reference_id : null;
    if (consentId === null || consentId.length === 0) continue;
    awaiting.push({
      consentId,
      title: typeof item.title === 'string' ? item.title : 'A written consent',
      consentDate: typeof item.occurred_on === 'string' ? item.occurred_on : null,
      committeeName:
        typeof item.committee_name === 'string' && item.committee_name.trim().length > 0
          ? item.committee_name.trim()
          : null,
    });
  }
  return awaiting;
}

/** Most recently opened first, so a consent circulated today is not below one from last year. */
export function sortConsents<T extends { created_at?: string | null }>(
  consents: readonly T[],
): T[] {
  return [...consents].sort((left, right) => instant(right.created_at) - instant(left.created_at));
}

/**
 * Human label for a consent status.
 *
 * `pending` is the plan's own word for a circulated consent short of unanimity, and it is deliberately
 * NOT rendered as "pending" to an officer: the word reads as a queue position rather than as the state
 * §4 requires be visible. "Waiting on signatures" says what is true and what would change it.
 */
export function consentStatusLabel(status: string | null | undefined): string {
  switch ((status ?? '').toLowerCase()) {
    case 'draft':
      return 'Draft';
    case 'pending':
      return 'Waiting on signatures';
    case 'executed':
      return 'Signed by everyone';
    case 'withdrawn':
      return 'Stopped';
    default:
      return humanize(status);
  }
}

/** Badge tone for a consent status. PURELY VISUAL — it decides a colour and nothing else. */
export function consentStatusTone(status: string | null | undefined): BadgeTone {
  switch ((status ?? '').toLowerCase()) {
    case 'executed':
      return 'success';
    case 'pending':
      return 'warning';
    case 'withdrawn':
      return 'neutral';
    default:
      return 'info';
  }
}

/** Whether this consent can still be edited in place (a draft's text is not frozen). */
export function isDraft(status: string | null | undefined): boolean {
  return (status ?? '').toLowerCase() === 'draft';
}

/** Whether this consent is circulated and short of unanimity — the state every §4 surface keys on. */
export function isAwaitingSignatures(status: string | null | undefined): boolean {
  return (status ?? '').toLowerCase() === 'pending';
}

/** Whether this consent reached a terminal state (executed or stopped); nothing may change from here. */
export function isSettled(status: string | null | undefined): boolean {
  const value = (status ?? '').toLowerCase();
  return value === 'executed' || value === 'withdrawn';
}

/** The factor that answered, where one did — the ONE factor-words function (`lib/factor-words`). */
export function authMethodLabel(method: string | null | undefined): string | null {
  return factorLabel(method);
}

/** Whether an entry in the log is a signature or the withdrawal of one. */
export function isSignatureAct(signature: BoardConsentSignatureOut): boolean {
  return (signature.act ?? '').toLowerCase() === 'signed';
}

/** Whether an entry in the log withdraws a signature (the wire's `revoked`). */
function isWithdrawalAct(signature: BoardConsentSignatureOut): boolean {
  return (signature.act ?? '').toLowerCase() === 'revoked';
}

/**
 * One entry in a seat's signature log, as ONE line: what happened and how it was captured (CBG-UX4).
 *
 * The capture class (`signature_class`) is always on the wire precisely so the weaker class is never
 * presented as the stronger one, and this line is what carries that onto the screen. A single
 * "Signed" for every class would erase the difference between a director acting in their own
 * session, an officer writing down what they were told, and a reply that came back by email (plan
 * §3.3). So the act and its capture are worded TOGETHER, here and nowhere else: composing an act word
 * with a capture phrase ("Signed · Signed by the director", "Signature withdrawn · Signed by the
 * director") says the verb twice, or contradicts it on a legal record.
 *
 * - A director's own act names the factor that answered. The database requires one on every act of
 *   that class, a withdrawal included, so a withdrawal names it too.
 * - An officer's entry names no factor (nobody re-proved anything); its required note has its own
 *   line on the page.
 * - `email_reply` is modelled but has no writer yet (the backend refuses it for both acts), so its two
 *   lines are unreachable today and worded for when the channel exists.
 * - A class or act this build does not know keeps both words, still without a repeat.
 *
 * ⚠ The wire field is named for its capture, not for the property it expresses: §3.4 bans that word's
 * root down to field names, and both the backend and this app took the same rename.
 */
export function signatureLogLine(signature: BoardConsentSignatureOut): string {
  return signatureActLine({
    act: signature.act,
    signatureClass: signature.signature_class,
    authMethod: signature.auth_method,
  });
}

/**
 * {@link signatureLogLine}'s words for any signature act, given its parts — the one composition the
 * consent log and the minutes signatures share (CBG-UX4), and the backend's `signature_act_words`
 * twin (the same act × class table is pinned on both sides).
 */
export function signatureActLine({
  act,
  signatureClass,
  authMethod,
}: {
  act: string | null | undefined;
  signatureClass: string | null | undefined;
  authMethod?: string | null;
}): string {
  const kind = (act ?? '').toLowerCase();
  const method = authMethodLabel(authMethod);
  const withMethod = (words: string) => (method !== null ? `${words}, ${method}` : words);
  const capture = (signatureClass ?? '').toLowerCase();
  if (kind === 'signed') {
    if (capture === 'step_up') return withMethod('Signed by the director');
    if (capture === 'manual_on_behalf') return 'Signature recorded by an officer on their behalf';
    if (capture === 'email_reply') return 'Signature returned by email';
    return withMethod(`Signed · ${humanize(signatureClass)}`);
  }
  if (kind === 'revoked') {
    if (capture === 'step_up') return withMethod('Withdrawn by the director');
    if (capture === 'manual_on_behalf') return 'Withdrawal recorded by an officer on their behalf';
    if (capture === 'email_reply') return 'Withdrawal returned by email';
    return withMethod(`Signature withdrawn · ${humanize(signatureClass)}`);
  }
  return withMethod(`${humanize(act)} · ${humanize(signatureClass)}`);
}

/**
 * The director's own standing on a consent, in the second person (CBG-UX4): their most recent act,
 * worded by how it was captured, like {@link signatureLogLine}.
 *
 * "You signed this" is true only of an act the director performed in their own session. An officer
 * may have recorded the director's paper or spoken assent (or its withdrawal) for them, and telling
 * the director they signed would present an officer's entry as the director's own act.
 */
export function mySignatureLine(signature: BoardConsentSignatureOut): string {
  const capture = (signature.signature_class ?? '').toLowerCase();
  if (isSignatureAct(signature)) {
    if (capture === 'step_up') return 'You signed this';
    if (capture === 'manual_on_behalf') return 'An officer recorded your signature on your behalf';
    if (capture === 'email_reply') return 'Your signature was returned by email';
    return 'Your signature is recorded';
  }
  if (isWithdrawalAct(signature)) {
    if (capture === 'step_up') return 'You withdrew your signature';
    if (capture === 'manual_on_behalf')
      return 'An officer recorded the withdrawal of your signature on your behalf';
    if (capture === 'email_reply') return 'Your withdrawal was returned by email';
    return 'The withdrawal of your signature is recorded';
  }
  return humanize(signature.act);
}

/**
 * How the director's own act was captured, in the second person, as the act now records it (CBG-SEC2,
 * contract §8.2): "with your passkey, confirmed at 10:15 AM". Null for an act the director didn't
 * perform in their own session, or one that names no factor. An act recorded before the signing
 * window carries no `factor_confirmed_at`, and names the factor alone.
 */
export function myCaptureLine(signature: BoardConsentSignatureOut): string | null {
  if ((signature.signature_class ?? '').toLowerCase() !== 'step_up') return null;
  const method = (signature.auth_method ?? '').trim().toLowerCase();
  const noun =
    method === 'passkey_uv'
      ? 'your passkey'
      : method === 'totp'
        ? 'your authenticator\u00a0app'
        : method === 'email_code'
          ? 'a code sent by email'
          : null;
  if (noun === null) return null;
  const confirmedAt = signature.factor_confirmed_at;
  return typeof confirmedAt === 'string' && confirmedAt.length > 0
    ? `with ${noun}, confirmed at ${displayClockTime(confirmedAt)}`
    : `with ${noun}`;
}

/**
 * The log for one seat, oldest first.
 *
 * Oldest-first because the log is a history and a withdrawal only makes sense read after the signature
 * it withdrew. Sorted by `(occurred_at, id)` — the same ordering, tiebreaker included, that the backend
 * reduces standing with, so the last row shown is the act that decides this seat's standing.
 */
export function actsForSeat(
  signatures: readonly BoardConsentSignatureOut[],
  rosterMemberId: string,
): BoardConsentSignatureOut[] {
  return signatures
    .filter((signature) => signature.roster_member_id === rosterMemberId)
    .sort((left, right) => {
      const byInstant = instant(left.occurred_at) - instant(right.occurred_at);
      return byInstant !== 0 ? byInstant : left.id.localeCompare(right.id);
    });
}

/** The seat as the officer's roster names it, with the non-voting seat called out. */
export function seatRoleLabel(role: string | null | undefined): string {
  switch ((role ?? '').toLowerCase()) {
    case 'chair':
      return 'Chair';
    case 'secretary':
      return 'Secretary';
    case 'observer':
      return 'Observer (non-voting)';
    case 'director':
      return 'Director';
    default:
      return 'Board member';
  }
}

/** Seats still to sign, in roster order — what the officer chases. */
export function outstandingSeats(
  roster: readonly BoardConsentRosterMemberOut[],
): BoardConsentRosterMemberOut[] {
  return roster.filter((seat) => seat.has_signed !== true);
}

/**
 * What the circulation's email did for one seat (CBG-OPS1-CIRC; backend contract §8.1), read
 * tolerantly. The wire carries CODES only, and this is the one place they become a state:
 *
 * - `null`: no `delivery`, as for a consent circulated before the email existed. The page says
 *   nothing rather than guess.
 * - `not_sent`: ANY reason, one this build doesn't know included. A newer code is still a mail that
 *   didn't go, and the officer's act is the same whatever it is (K-C1).
 * - `sent`: the mail service accepted it, at `sentAt`. `unjoinedThen` is true only where the wire
 *   says no account had joined the seat when it went, which is which words it was sent.
 * - `not_confirmed`: the mail was to go and no outcome was recorded (the backend's mark failed).
 *
 * A reason outranks `email_sent`. The backend's CHECK never lets a row carry both; if one ever did,
 * the line asking the officer to act claims less than "Emailed" would.
 */
export type SeatDelivery =
  | { state: 'sent'; sentAt: string | null; unjoinedThen: boolean }
  | { state: 'not_sent' }
  | { state: 'not_confirmed' };

export function seatDelivery(seat: BoardConsentRosterMemberOut): SeatDelivery | null {
  return deliveryOf(seat.delivery);
}

/**
 * The same reading of any served `delivery` (the wire's `BoardConsentDeliveryOut`): a consent seat's,
 * or an asked director's on a closed session's minutes, whose ask emails them the same way
 * (CBG-RC10-MAIL, backend `board_minutes.md` §S17.5).
 */
export function deliveryOf(raw: unknown): SeatDelivery | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  if (row.not_sent_reason !== null && row.not_sent_reason !== undefined) {
    return { state: 'not_sent' };
  }
  if (row.email_sent === true) {
    return {
      state: 'sent',
      sentAt: typeof row.sent_at === 'string' ? row.sent_at : null,
      unjoinedThen: row.seat_connected === false,
    };
  }
  return { state: 'not_confirmed' };
}

/**
 * The seats still unjoined on the LIVE roster, by id (CBG-OPS1-CIRC-WEB). "Hasn’t joined Board yet"
 * is present tense, while `seat_connected` is the seat as it was when the mail went, so the page
 * says it only of a seat that is still unjoined now. `null`, the read pending or failed, is the
 * empty set: Board says nothing it hasn't read.
 */
export function seatsStillUnjoined(live: readonly OrgBoardMemberOut[] | null): ReadonlySet<string> {
  if (live === null) return new Set();
  return new Set(live.filter((seat) => seatConnection(seat) !== 'joined').map((seat) => seat.id));
}

function instant(iso: string | null | undefined): number {
  if (!iso) return 0;
  const value = new Date(iso).getTime();
  return Number.isNaN(value) ? 0 : value;
}

/**
 * The committee a written consent is of (CBG-YR6-CONSENT), by its name as the registry holds it now —
 * served beside `committee_id` on every consent read — or null for the board's own. A committee's
 * consent whose name didn't come is still a committee's.
 */
export function consentCommitteeName(
  consent: { committee_id?: string | null; committee_name?: string | null } | null | undefined,
): string | null {
  if (!consent?.committee_id) return null;
  const name = (consent.committee_name ?? '').trim();
  return name.length > 0 ? name : cm.aCommittee;
}
